import { useQuery } from "@apollo/client";
import { Callout as CalloutType } from "../../graphql/schema";
import {
  tokens,
  makeStyles,
  shorthands,
  Text,
} from "@fluentui/react-components";
import { GET_CALLOUT } from "../../graphql/queries";
import { CtaButton } from "../common/input/CtaButton";
import { Title } from "../common/text/Title";
import { useNavigate } from "react-router-dom";
import { clarityEvent } from "../../lib/clarity";
import { useCmsLocale } from "../../i18n/cmsLocale";

const useStyles = makeStyles({
  wrapper: {
    boxShadow: tokens.shadow8,
  },
  contentContainer: {
    maxWidth: "1400px",
    width: "100%",
    ...shorthands.margin(0, "auto"),
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  text: {
    position: "relative",
    maxWidth: "1000px",
    marginRight: tokens.spacingHorizontalXXL,
    "@media screen and (max-width: 1450px)": { maxWidth: "600px" },
    minHeight: "200px",
    color: tokens.colorBrandBackground,
    ...shorthands.padding(
      tokens.spacingHorizontalXL,
      tokens.spacingHorizontalXXXL
    ),
  },
  shapeWrapper: {
    position: "relative",
    width: "80%",
  },
  shape: {
    position: "absolute",
    left: "-9999px",
    right: "0",
    paddingLeft: "9999px",
    backgroundColor: "#C9E3FF",
    opacity: 0.8,
    borderTopRightRadius: "45px",
    borderBottomRightRadius: "180px",
    top: 0,
    bottom: 0,
  },
  textWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  centerScreen: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    position: "relative",
    minHeight: "655px",
    zIndex: 1,
  },
  marginBottom: {
    marginBottom: tokens.spacingVerticalXL,
  },
});

export const CalloutSection = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const locale = useCmsLocale();

  const { data, loading, error } = useQuery(GET_CALLOUT, { variables: { locale } });
  if (loading || error || !data.callout) return <></>;
  const calloutData: CalloutType = data.callout;

  return (
    <article
      className={classes.wrapper}
      style={{ position: "relative", overflow: "hidden", background: "#0d1b2e" }}
    >
      {/* Decorative SVG — fluid filled shapes */}
      <svg
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
        viewBox="0 0 1440 600"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background layer — very subtle, spread across */}
        <path d="M 60 80 C 140 20, 280 60, 300 160 C 320 260, 200 320, 100 280 C 0 240, -20 140, 60 80 Z" fill="rgba(25,55,100,0.18)" />
        <path d="M 620 40 C 700 -20, 820 10, 840 100 C 860 190, 760 240, 680 210 C 600 180, 560 100, 620 40 Z" fill="rgba(25,55,100,0.12)" />
        <path d="M 400 420 C 500 360, 640 390, 660 490 C 680 580, 560 630, 440 600 C 320 570, 300 480, 400 420 Z" fill="rgba(25,55,100,0.14)" />
        <path d="M 900 480 C 980 430, 1060 460, 1040 550 C 1020 630, 920 650, 860 600 C 800 550, 820 530, 900 480 Z" fill="rgba(25,55,100,0.1)" />

        {/* Foreground anchor — large confident shape filling the right half */}
        <path
          d="M 1440 -20 C 1360 40, 1200 20, 1160 160 C 1120 300, 1220 440, 1340 480 C 1460 520, 1560 440, 1580 300 C 1600 160, 1540 60, 1440 -20 Z"
          fill="rgba(25,55,100,0.10)"
        />
        {/* Smaller companion — bottom right, partially off screen */}
        <path
          d="M 1300 520 C 1360 470, 1460 490, 1480 560 C 1500 630, 1440 690, 1340 670 C 1240 650, 1230 570, 1300 520 Z"
          fill="rgba(25,55,100,0.22)"
        />
      </svg>

      <section className={classes.centerScreen}>
        <div className={classes.contentContainer}>
          <div className={classes.shapeWrapper}>
            <div className={classes.shape}></div>
            <div className={classes.text}>
              <div>
                <Title as="h2">{calloutData.title ?? ""}</Title>
              </div>
              {calloutData.description && (
                <div className={classes.textWrapper}>
                  <Text size={500} className={classes.marginBottom}>
                    {calloutData.description}
                  </Text>
                  <Text size={500} weight="medium">
                    {calloutData.descriptionPt2 ?? ""}
                  </Text>
                </div>
              )}
              {calloutData.buttonText && (
                <CtaButton
                  buttonText={calloutData.buttonText}
                  onClick={() => { clarityEvent("hero_cta_clicked"); navigate("/order"); }}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};
